import type { Formats, TranslationValues } from 'next-intl'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { LFS_URL } from '@web/utils/env'

export const contentSearchPages = [
  AppRoutes.BACKGROUNDS_SEARCH,
  AppRoutes.BORDERS_AND_CORNERS_SEARCH,
  AppRoutes.CARTOONS_SEARCH,
  AppRoutes.CLIP_ART_SEARCH,
  AppRoutes.DRAWINGS_SEARCH,
  AppRoutes.FRAMES_SEARCH,
  AppRoutes.MOCKUPS_SEARCH,
  AppRoutes.PATTERNS_SEARCH,
  AppRoutes.QUOTES_SEARCH,
  AppRoutes.SILHOUETTES_SEARCH,
  AppRoutes.STICKERS_SEARCH,
  AppRoutes.SKETCHES_SEARCH,
  AppRoutes.SVGS_SEARCH,
  AppRoutes.TEXTURES_SEARCH,
  AppRoutes.PNG_SEARCH,
  AppRoutes.STOCKPHOTOS_SEARCH,
]

export type ContentCategory =
  | 'stock photos'
  | 'PNGs'
  | 'Kawaiis'
  | 'Mockups'
  | 'Backgrounds'
  | 'Sketches'
  | 'Patterns'
  | 'Border and Corners'
  | 'Frames'
  | 'Cartoons'
  | 'Textures'
  | 'Silhouettes'
  | 'Drawings'
  | 'Clip Art'
  | 'Quotes'
  | 'Stickers'
  | 'SVGs'
  | 'Coloring pages'

export const PAGE_CATEGORIES = [
  'png',
  'kawaii',
  'mockups',
  'backgrounds',
  'sketches',
  'patterns',
  'borders-and-corners',
  'frames',
  'cartoons',
  'textures',
  'silhouettes',
  'drawings',
  'clip-art',
  'quotes',
  'stickers',
  'svgs',
  'stockphotos',
  'coloring-pages',
] as const

export type CategoryPageType = (typeof PAGE_CATEGORIES)[number]

export const isCategoryPageType = (value: string): value is CategoryPageType => {
  return PAGE_CATEGORIES.includes(value as CategoryPageType)
}

export interface PageDetails {
  authorId: string
  parentId: string
  categoryName: string
  homepage: string
  browseMore: string
  searchPage: string
  linkBlockId: string
  contentCategory: ContentCategory
  pdpLinkBlockId: string
}

export function isCategory(value: string): value is CategoryPageType {
  return PAGE_CATEGORIES.includes(value as CategoryPageType)
}

export const mapCategoryPageTypeToCategoryDetails: Record<CategoryPageType, PageDetails> = {
  png: {
    authorId: 'pngs',
    parentId: 'lohUUQUWL',
    categoryName: 'PNG',
    homepage: AppRoutes.PNG,
    browseMore: AppRoutes.PNG_POPULAR,
    searchPage: AppRoutes.PNG_SEARCH,
    linkBlockId: '2UkAsWo7xarSJh12khpdwGZeHjX',
    contentCategory: 'PNGs',
    pdpLinkBlockId: '2UTOCOIGFrZzpBz8CmgQVvM2K5s',
  },
  kawaii: {
    authorId: 'kawaii',
    parentId: 'NQiC1yItM',
    categoryName: 'Kawaiis',
    homepage: AppRoutes.KAWAII,
    browseMore: AppRoutes.KAWAII_POPULAR,
    searchPage: AppRoutes.KAWAII_SEARCH,
    linkBlockId: '2eUIxbkbZxlFnbw6OJWKqRz7LIY',
    contentCategory: 'Kawaiis',
    pdpLinkBlockId: '2eUIxbkbZxlFnbw6OJWKqRz7LIY',
  },
  mockups: {
    authorId: 'mockups',
    parentId: 'FYK7LrRLd',
    categoryName: 'Mockups',
    homepage: AppRoutes.MOCKUPS,
    browseMore: AppRoutes.MOCKUPS_POPULAR,
    searchPage: AppRoutes.MOCKUPS_SEARCH,
    linkBlockId: '2bAnSE9wB9snbgNZ3yCmJe1z0Ws',
    contentCategory: 'Mockups',
    pdpLinkBlockId: '2bAnSE9wB9snbgNZ3yCmJe1z0Ws',
  },
  backgrounds: {
    authorId: 'backgrounds',
    parentId: 'fBU2UowC9',
    categoryName: 'Backgrounds',
    homepage: AppRoutes.BACKGROUNDS,
    browseMore: AppRoutes.BACKGROUNDS_POPULAR,
    searchPage: AppRoutes.BACKGROUNDS_SEARCH,
    linkBlockId: '2bAnvR3aqCUQ5ThgmgAAhreiDjj',
    contentCategory: 'Backgrounds',
    pdpLinkBlockId: '2bAnvR3aqCUQ5ThgmgAAhreiDjj',
  },
  sketches: {
    authorId: 'sketches',
    parentId: 'BmQ1JekeE',
    categoryName: 'Sketches',
    homepage: AppRoutes.SKETCHES,
    browseMore: AppRoutes.SKETCHES_POPULAR,
    searchPage: AppRoutes.SKETCHES_SEARCH,
    linkBlockId: '2bAplIMp17Q1zx3njSnxQlc3Cya',
    contentCategory: 'Sketches',
    pdpLinkBlockId: '2bAplIMp17Q1zx3njSnxQlc3Cya',
  },
  patterns: {
    authorId: 'patterns',
    parentId: 'p2DaL9jrp',
    categoryName: 'Patterns',
    homepage: AppRoutes.PATTERNS,
    browseMore: AppRoutes.PATTERNS_POPULAR,
    searchPage: AppRoutes.PATTERNS_SEARCH,
    linkBlockId: '2bApolMhV9LLs172rUOJCMmyRuS',
    contentCategory: 'Patterns',
    pdpLinkBlockId: '2bApolMhV9LLs172rUOJCMmyRuS',
  },
  'borders-and-corners': {
    authorId: 'borders-and-corners',
    parentId: 'Grrz4AP6O',
    categoryName: 'Border and Corners',
    homepage: AppRoutes.BORDERS_AND_CORNERS,
    browseMore: AppRoutes.BORDERS_AND_CORNERS_POPULAR,
    searchPage: AppRoutes.BORDERS_AND_CORNERS_SEARCH,
    linkBlockId: '2bAptEIH4XrhDcYxpdwDHhwjKQf',
    contentCategory: 'Border and Corners',
    pdpLinkBlockId: '2bAptEIH4XrhDcYxpdwDHhwjKQf',
  },
  frames: {
    authorId: 'frames',
    parentId: '7otheGtCW',
    categoryName: 'Frames',
    homepage: AppRoutes.FRAMES,
    browseMore: AppRoutes.FRAMES_POPULAR,
    searchPage: AppRoutes.FRAMES_SEARCH,
    linkBlockId: '2bApwy5ffNdH459TST4NvUPt5Md',
    contentCategory: 'Frames',
    pdpLinkBlockId: '2bApwy5ffNdH459TST4NvUPt5Md',
  },
  cartoons: {
    authorId: 'cartoons',
    parentId: 'sD6j3z7oS',
    categoryName: 'Cartoons',
    homepage: AppRoutes.CARTOONS,
    browseMore: AppRoutes.CARTOONS_POPULAR,
    searchPage: AppRoutes.CARTOONS_SEARCH,
    linkBlockId: '2bAq0G61pM4iQqqM8MYBIT8KEKa',
    contentCategory: 'Cartoons',
    pdpLinkBlockId: '2bAq0G61pM4iQqqM8MYBIT8KEKa',
  },
  textures: {
    authorId: 'textures',
    parentId: 'KAQenoRt3',
    categoryName: 'Textures',
    homepage: AppRoutes.TEXTURES,
    browseMore: AppRoutes.TEXTURES_POPULAR,
    searchPage: AppRoutes.TEXTURES_SEARCH,
    linkBlockId: '2bAq3X1uA6DCI2AHLmO1WvM29K1',
    contentCategory: 'Textures',
    pdpLinkBlockId: '2bAq3X1uA6DCI2AHLmO1WvM29K1',
  },
  silhouettes: {
    authorId: 'silhouettes',
    parentId: 'gFrC94WAl',
    categoryName: 'Silhouettes',
    homepage: AppRoutes.SILHOUETTES,
    browseMore: AppRoutes.SILHOUETTES_POPULAR,
    searchPage: AppRoutes.SILHOUETTES_SEARCH,
    linkBlockId: '2bAqHc67idFxrCZ6SaRsAr1fJZJ',
    contentCategory: 'Silhouettes',
    pdpLinkBlockId: '2bAqHc67idFxrCZ6SaRsAr1fJZJ',
  },
  drawings: {
    authorId: 'drawings',
    parentId: '0cisT9V5O',
    categoryName: 'Drawings',
    homepage: AppRoutes.DRAWINGS,
    browseMore: AppRoutes.DRAWINGS_POPULAR,
    searchPage: AppRoutes.DRAWINGS_SEARCH,
    linkBlockId: '2bAqLD9saUy5PlGmT326Ngz1zgm',
    contentCategory: 'Drawings',
    pdpLinkBlockId: '2bAqLD9saUy5PlGmT326Ngz1zgm',
  },
  'clip-art': {
    authorId: 'clip-art',
    parentId: 'mUe4UzBk9',
    categoryName: 'Clip Art',
    homepage: AppRoutes.CLIP_ART,
    browseMore: AppRoutes.CLIP_ART_POPULAR,
    searchPage: AppRoutes.CLIP_ART_SEARCH,
    linkBlockId: '2bAqOs2ifKRzYvyfQ3ndVe8ADKA',
    contentCategory: 'Clip Art',
    pdpLinkBlockId: '2bAqOs2ifKRzYvyfQ3ndVe8ADKA',
  },
  quotes: {
    authorId: 'quotes',
    parentId: 'tV06AU87u',
    categoryName: 'Quotes',
    homepage: AppRoutes.QUOTES,
    browseMore: AppRoutes.QUOTES_POPULAR,
    searchPage: AppRoutes.QUOTES_SEARCH,
    linkBlockId: '2crqOOER9SCamphCuej13Se8CgC',
    contentCategory: 'Quotes',
    pdpLinkBlockId: '2crqOOER9SCamphCuej13Se8CgC',
  },
  stickers: {
    authorId: 'stickers',
    parentId: 'qIn5Sdma7',
    categoryName: 'Stickers',
    homepage: AppRoutes.STICKERS,
    browseMore: AppRoutes.STICKERS_POPULAR,
    searchPage: AppRoutes.STICKERS_SEARCH,
    linkBlockId: '2bAqTFL3bJGRdhFOAfwn7CeNygI',
    contentCategory: 'Stickers',
    pdpLinkBlockId: '2bAqTFL3bJGRdhFOAfwn7CeNygI',
  },
  svgs: {
    authorId: 'svgs',
    parentId: 'L9LeUsIul',
    categoryName: 'SVGs',
    homepage: AppRoutes.SVGS,
    browseMore: AppRoutes.SVGS_POPULAR,
    searchPage: AppRoutes.SVGS_SEARCH,
    linkBlockId: '2bAqWpkZ18ktBEMdObeHsbSP9CO',
    contentCategory: 'SVGs',
    pdpLinkBlockId: '2bAqWpkZ18ktBEMdObeHsbSP9CO',
  },
  stockphotos: {
    authorId: 'stockphotos',
    parentId: 'bBYblLLTK',
    categoryName: 'Stock photos',
    homepage: AppRoutes.STOCKPHOTOS,
    browseMore: AppRoutes.STOCKPHOTOS_POPULAR,
    searchPage: AppRoutes.STOCKPHOTOS_SEARCH,
    linkBlockId: '2dDx4KpPzABGd4HSjwA1eTD0CnY',
    contentCategory: 'stock photos',
    pdpLinkBlockId: '2dDx4KpPzABGd4HSjwA1eTD0CnY',
  },
  'coloring-pages': {
    authorId: 'coloring-pages',
    parentId: 'CJOftDuBI',
    categoryName: 'Coloring pages',
    homepage: AppRoutes.COLORING_PAGES,
    browseMore: AppRoutes.COLORING_PAGES_POPULAR,
    searchPage: AppRoutes.COLORING_PAGES_SEARCH,
    linkBlockId: '2crskwmH5Rj792DXQPJUWQuQv5U',
    contentCategory: 'Coloring pages',
    pdpLinkBlockId: '2crskwmH5Rj792DXQPJUWQuQv5U',
  },
}

const transform = (obj: Record<CategoryPageType, PageDetails>): Record<string, PageDetails> => {
  const newObject: Record<string, PageDetails> = {}
  Object.keys(obj).forEach(key => {
    const details = obj[key as CategoryPageType]
    newObject[details.parentId] = details
  })
  return newObject
}

export const mapCategoryIdToCategoryPageDetails = transform(mapCategoryPageTypeToCategoryDetails)

export const STOCKPHOTOS_AUTHOR_ID = 'stockphotos'

export function getCategoryDetails(
  categoryId: string,
  t: <TargetKey extends string>(
    key: TargetKey,
    values?: TranslationValues | undefined,
    formats?: Partial<Formats> | undefined,
  ) => string,
) {
  switch (categoryId) {
    case 'lohUUQUWL': {
      return {
        label: t('categories.png'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['png'],
      }
    }
    case 'FYK7LrRLd': {
      return {
        label: t('categories.mockup'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['jpg', 'jpeg'],
      }
    }
    case 'NQiC1yItM': {
      return {
        label: t('categories.kawaii'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['png'],
      }
    }
    case 'sD6j3z7oS': {
      return {
        label: t('categories.cartoon'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['png'],
      }
    }
    case 'fBU2UowC9': {
      return {
        label: t('categories.background'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['jpg', 'jpeg'],
      }
    }
    case 'p2DaL9jrp': {
      return {
        label: t('categories.pattern'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['png'],
      }
    }
    case '7otheGtCW': {
      return {
        label: t('categories.frame'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['svg'],
      }
    }
    case 'tV06AU87u': {
      return {
        label: t('categories.quote'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['svg'],
      }
    }
    case 'Grrz4AP6O': {
      return {
        label: t('categories.border_or_corner'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['png'],
      }
    }
    case 'BmQ1JekeE': {
      return {
        label: t('categories.sketch'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['png'],
      }
    }
    case 'CJOftDuBI': {
      return {
        label: t('categories.coloring_page'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['pdf'],
      }
    }
    case 'bBYblLLTK': {
      return {
        label: t('categories.stockphoto'),
        labelPlural: getCategoryPlural(categoryId, t),
        formats: ['jpg'],
      }
    }
    default: {
      return {
        label: 'image',
        labelPlural: 'images',
        formats: [],
      }
    }
  }
}

export function getCategoryPlural(
  categoryId: string,
  t: <TargetKey extends string>(
    key: TargetKey,
    values?: TranslationValues | undefined,
    formats?: Partial<Formats> | undefined,
  ) => string,
) {
  switch (categoryId) {
    case 'lohUUQUWL': {
      return t('categories.pngs')
    }
    case 'FYK7LrRLd': {
      return t('categories.mockups')
    }
    case 'NQiC1yItM': {
      return t('categories.kawaii_images')
    }
    case 'sD6j3z7oS': {
      return t('categories.cartoons')
    }
    case 'fBU2UowC9': {
      return t('categories.backgrounds')
    }
    case 'p2DaL9jrp': {
      return t('categories.patterns')
    }
    case '7otheGtCW': {
      return t('categories.frames')
    }
    case 'tV06AU87u': {
      return t('categories.quotes')
    }
    case 'Grrz4AP6O': {
      return t('categories.borders_or_corners')
    }
    case 'BmQ1JekeE': {
      return t('categories.sketches')
    }
    case 'CJOftDuBI': {
      return t('categories.coloring_pages')
    }
    case 'bBYblLLTK': {
      return t('categories.stockphotos')
    }
    default: {
      return 'images'
    }
  }
}

export const ogBannerPNGImageUrl = `${LFS_URL}/web/pages/content/png/PNG-Page_banner_OGimage.jpg`
export const ogBannerQuotesImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_14-quotes-2-1.png'
export const ogBannerSilhouettesImageUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_17-silhouette-1-1.png'
export const ogBannerSketchImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_06-Sketch-1.png'
export const ogBannerFramesImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_11-Frame-1.png'
export const ogBannerDrawingImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_16-drawing-1.png'
export const ogBannerColoringPagesImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_08-coloringpages-1.png'
export const ogBannerBordersAndCornersImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_10-order_corner-1.png'
export const ogBannerCartoonImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_05-Cartoon-1.png'
export const ogBannerSVGImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_13-SVG-1.png'
export const ogBannerKawaiiImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_02-Kawaii-1.png'
export const ogBannerBackgroundImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_09-background-1.png'
export const ogBannerMockupImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_04-Mockuptemplate-1.png'
export const ogBannerClipArtImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_12-clipart-1.png'
export const ogBannerStockphotosImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_07-stockphoto-1.png'
export const ogBannerPatternImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_03-Pattern-1.png'
export const ogBannerTextureImagesUrl =
  'https://www.creativefabrica.com/wp-content/uploads/2024/08/22/CF_FB-OG-Image_Categories_15-texure-1.png'

export function getCategoryDetailsById(id?: string) {
  if (!id) {
    return null
  }
  const data = Object.keys(mapCategoryPageTypeToCategoryDetails).find(key => {
    return mapCategoryPageTypeToCategoryDetails[key as CategoryPageType].parentId === id
  })
  return mapCategoryPageTypeToCategoryDetails[data as CategoryPageType]
}

export function getIsProductFree(categoryParentId?: string) {
  return categoryParentId === mapCategoryPageTypeToCategoryDetails['coloring-pages'].parentId
}
